// @flow

import React from 'react';
import { Switch, Button, H1, Alert, Intent } from '@blueprintjs/core';
import styled from 'styled-components';

import WidgetGrid from './widget-grid';
import TopBarActions from './top-bar-actions';
import LocationFilterButtons from '../../location-filter-buttons';
import ScheduledReportJobExplorer from '../../scheduled-report-job-explorer';
import * as Selectors from '../../../selectors';
import {
  DemographicsPanelHeader,
  DemographicsBar,
  SwitchContainer,
  EmailButton,
} from '../summary/layout';

const StyledH1 = styled(H1)`
  color: var(--report-heading-text);
  font-family: var(--fonts-heading);
  max-width: var(--widget-max-width);
  width: 100%;
  text-align: center;
  margin: 0.5rem auto 2rem;
`;

type ReportContentProps = {
  report: Object,
  reportConfig: Object,
  topLevelConfig: Object,
  allLocationsList: Array<Object>,
  availableMetrics: Object,
  startOfWeek: 'monday' | 'sunday',
  showStaffFilters: boolean,
  fetchingQueryData: boolean,
  history: Object,
  updateReportConfig: (args: Object) => void,
  handleTopLevelExcludeStaff: () => void,
  openScheduledReportJobExplorer: () => void,
  canShowEdit: boolean,
  canShowShare: boolean,
  canShowDelete: boolean,
  onEdit: () => void,
  onShare: () => void,
  onDelete: () => void,
  onNewChat: () => void,
  perms: Object,
  onPermissionsChange: (perms: Object) => void,
  showDeleteConfirm: boolean,
  showShareConfirm: boolean,
  confirmDelete: () => void,
  confirmShare: () => void,
  setShowDeleteConfirm: (show: boolean) => void,
  setShowShareConfirm: (show: boolean) => void,
  allReports: Array<Object>,
  isScheduledReportJobExplorerOpen: boolean,
  closeScheduledReportJobExplorer: () => void,
  getHierarchyConfig: (parent: Object, child: Object) => Object,
  transformHierarchyConfig: (config: Object) => Object,
  isOrgReports: boolean,
};

const ReportContent = ({
  report,
  topLevelConfig,
  allLocationsList,
  availableMetrics,
  startOfWeek,
  showStaffFilters,
  fetchingQueryData,
  updateReportConfig,
  handleTopLevelExcludeStaff,
  openScheduledReportJobExplorer,
  canShowEdit,
  canShowShare,
  canShowDelete,
  onEdit,
  onShare,
  onDelete,
  onNewChat,
  perms,
  onPermissionsChange,
  showDeleteConfirm,
  showShareConfirm,
  confirmDelete,
  confirmShare,
  setShowDeleteConfirm,
  setShowShareConfirm,
  allReports,
  isScheduledReportJobExplorerOpen,
  closeScheduledReportJobExplorer,
  getHierarchyConfig,
  transformHierarchyConfig,
  isOrgReports,
}: ReportContentProps): React$Node => {
  return (
    <div style={{ overflow: 'auto', height: '100%', width: '100%' }}>
      <TopBarActions
        canShowEdit={canShowEdit}
        canShowShare={canShowShare}
        canShowDelete={canShowDelete}
        onEdit={onEdit}
        onShare={onShare}
        onDelete={onDelete}
        onNewChat={onNewChat}
        deleteModalOpen={showDeleteConfirm}
        shareModalOpen={showShareConfirm}
        onPermissionsChange={onPermissionsChange}
        report={report}
      />
      <StyledH1>{report.name}</StyledH1>
      <DemographicsBar>
        <DemographicsPanelHeader>
          {showStaffFilters && (
            <div>
              <SwitchContainer>
                <Switch
                  disabled={fetchingQueryData}
                  checked={Selectors.isDemographicFilterRoleCustomer(topLevelConfig)}
                  label="Exclude staff from counts"
                  onChange={handleTopLevelExcludeStaff}
                />
              </SwitchContainer>
            </div>
          )}
          <div>
            <LocationFilterButtons
              disabled={fetchingQueryData}
              locations={allLocationsList}
              filteredLocations={topLevelConfig.locations}
              updateFilteredLocations={(locations) =>
                updateReportConfig({
                  property: 'locations',
                  newValue: locations,
                  hierarchyLevel: 0,
                  hierarchyIndex: 0,
                })
              }
            />
          </div>
          <EmailButton>
            <Button
              icon="notifications"
              onClick={openScheduledReportJobExplorer}
            >
              Email schedule
            </Button>
          </EmailButton>
        </DemographicsPanelHeader>
      </DemographicsBar>
      <WidgetGrid
        report={report}
        getHierarchyConfig={getHierarchyConfig}
        transformHierarchyConfig={transformHierarchyConfig}
        updateReportConfig={updateReportConfig}
        availableMetrics={availableMetrics}
        allLocationsList={allLocationsList}
        startOfWeek={startOfWeek}
      />
      <ScheduledReportJobExplorer
        reports={allReports}
        isOpen={isScheduledReportJobExplorerOpen}
        onClose={closeScheduledReportJobExplorer}
      />
      <Alert
        cancelButtonText="Cancel"
        confirmButtonText="Delete Report"
        icon="trash"
        intent={Intent.DANGER}
        isOpen={!!showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
      >
        <p>
          {isOrgReports
            ? 'Deleting this report will delete it from the Organisation Reports menu for all of the users within your company. Are you sure you want to delete this report?'
            : 'Are you sure you want to delete this report?'}
        </p>
      </Alert>
      <Alert
        cancelButtonText="Cancel"
        confirmButtonText="Share Report"
        icon="share"
        intent={Intent.PRIMARY}
        isOpen={!!showShareConfirm}
        onCancel={() => setShowShareConfirm(false)}
        onConfirm={confirmShare}
      >
        <p>
          {'Sharing this report will remove it from My Reports and make it available to all users via Organisation Reports. Are you sure you want to share this report?'}
        </p>
      </Alert>
    </div>
  );
};

export default ReportContent;
